import { useEffect, useState } from "react";

export interface LiveRates {
  thirtyYear: string;
  fifteenYear: string;
  fha: string;
  va: string;
  asOf: string;
  source: string;
}

// FHA and VA are manually set — Freddie Mac only publishes 30-yr and 15-yr
const MANUAL_FHA = "6.91";
const MANUAL_VA = "6.92";
const MANUAL_RATES_CACHE_KEY = `${MANUAL_FHA}-${MANUAL_VA}`;

const FALLBACK: LiveRates = {
  thirtyYear: "7.24",
  fifteenYear: "6.60",
  fha: MANUAL_FHA,
  va: MANUAL_VA,
  asOf: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
  source: "Freddie Mac Primary Mortgage Market Survey",
};

const CACHE_KEY = `hm-live-rates:${MANUAL_RATES_CACHE_KEY}`;
const CACHE_TTL = 1000 * 60 * 60; // 1 hour

/**
 * The rate snapshot is a single unauthenticated function call, so it is made
 * with plain `fetch` instead of the Supabase JS client. That keeps ~40 KiB of
 * client library out of the homepage's critical path. Same endpoint, same data.
 */
const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-rates`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

type CacheEntry = { at: number; rates: LiveRates };

let memoryCache: CacheEntry | null = null;
let inflight: Promise<LiveRates> | null = null;

const readSessionCache = (): CacheEntry | null => {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed?.rates || typeof parsed.at !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeSessionCache = (entry: CacheEntry) => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* storage full or blocked — memory cache still applies */
  }
};

async function fetchRates(): Promise<LiveRates> {
  try {
    const res = await fetch(FUNCTIONS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
        Authorization: `Bearer ${ANON_KEY}`,
      },
      body: "{}",
    });
    if (!res.ok) throw new Error(`get-rates responded ${res.status}`);
    const data = await res.json();

    const asOfDate = data?.asOf
      ? new Date(data.asOf + "T00:00:00").toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : FALLBACK.asOf;

    return {
      thirtyYear: typeof data?.thirtyYear === "number" ? data.thirtyYear.toFixed(2) : FALLBACK.thirtyYear,
      fifteenYear: typeof data?.fifteenYear === "number" ? data.fifteenYear.toFixed(2) : FALLBACK.fifteenYear,
      fha: MANUAL_FHA,
      va: MANUAL_VA,
      asOf: asOfDate,
      source: data?.source ?? FALLBACK.source,
    };
  } catch (err) {
    console.warn("Failed to fetch live rates, using fallback:", err);
    return FALLBACK;
  }
}

function getRates(): Promise<LiveRates> {
  const cached = memoryCache ?? readSessionCache();
  if (cached && Date.now() - cached.at < CACHE_TTL) {
    memoryCache = cached;
    return Promise.resolve(cached.rates);
  }
  if (inflight) return inflight;

  inflight = fetchRates().then((rates) => {
    const entry: CacheEntry = { at: Date.now(), rates };
    memoryCache = entry;
    writeSessionCache(entry);
    inflight = null;
    return rates;
  });
  return inflight;
}

/**
 * Returns the current market rate snapshot. Renders immediately with the
 * fallback figures (or the cached response) so nothing pops in, then swaps to
 * the live values once the request resolves.
 */
export function useLiveRates(): { data: LiveRates } {
  const [data, setData] = useState<LiveRates>(() => {
    const cached = memoryCache ?? readSessionCache();
    return cached?.rates ?? FALLBACK;
  });

  useEffect(() => {
    let active = true;
    getRates().then((rates) => {
      if (active) setData(rates);
    });
    return () => {
      active = false;
    };
  }, []);

  return { data };
}
